import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { KeyRound, Lock, Mail } from 'lucide-react';
import { loginSchema } from '@gbv/types';
import { AuthButton, AuthError, AuthField, AuthLayout, AuthLink } from '@gbv/ui';
import { useAuth } from '../stores/auth';
import { ApiRequestError } from '../lib/api';

/**
 * Admin sign-in.
 *
 * NOTE ON THE AUTHENTICATION CODE FIELD — read before relying on it.
 * The design specifies a 6-digit code and a "Protected by 2-factor
 * authentication" line. The field is rendered and its value is sent to the
 * API, but no admin has a 2FA secret enrolled yet, so nothing verifies it and
 * a correct email plus password still signs in on its own. It is therefore
 * OPTIONAL here on purpose: making it required would lock every existing admin
 * out. Enforcing it needs TOTP enrolment, secret storage and recovery codes on
 * the backend first.
 */
const adminLoginSchema = loginSchema.extend({
  authCode: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator.')
    .optional()
    .or(z.literal('')),
});

type AdminLoginInput = z.infer<typeof adminLoginSchema>;

export function LoginPage() {
  const login = useAuth((s) => s.login);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AdminLoginInput>({
    resolver: zodResolver(adminLoginSchema),
    defaultValues: { email: '', password: '', authCode: '' },
  });

  async function onSubmit(values: AdminLoginInput): Promise<void> {
    setFormError(null);
    try {
      await login(values.email, values.password, values.authCode || undefined);
    } catch (err) {
      setFormError(
        err instanceof ApiRequestError
          ? err.message
          : 'We could not reach the server. Check your connection and try again.',
      );
    }
  }

  return (
    <AuthLayout
      badge="Admin Console"
      headline={
        <>
          Oversee everything.
          <br />
          Control access.
        </>
      }
      description="Manage employees, approve posts, review recordings and monitor billing across every client account."
      features={[
        'Employee & client management',
        'Social post approvals',
        'Call recordings & QA',
        'Billing & usage insights',
      ]}
      cardTitle="Welcome back"
      cardDescription="Restricted access — Super Admin & Admins only."
      footnote="Protected by 2-factor authentication"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <AuthField
          label="Email address"
          type="email"
          inputMode="email"
          autoComplete="username"
          autoFocus
          placeholder="you@company.com"
          icon={<Mail size={14} />}
          error={errors.email?.message}
          {...register('email')}
        />

        <AuthField
          label="Password"
          type="password"
          autoComplete="current-password"
          placeholder="••••••••••"
          icon={<Lock size={14} />}
          error={errors.password?.message}
          {...register('password')}
        />

        <div className="flex flex-col gap-1.5">
          <AuthField
            label="Authentication code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="6-digit code"
            icon={<KeyRound size={14} />}
            error={errors.authCode?.message}
            {...register('authCode')}
          />
          <div className="flex justify-end">
            <AuthLink>Forgot password?</AuthLink>
          </div>
        </div>

        {formError && <AuthError>{formError}</AuthError>}

        <AuthButton isLoading={isSubmitting}>Sign in to admin console</AuthButton>
      </form>

      {import.meta.env.DEV && (
        <p className="mt-4 text-center font-mono text-[11px] text-auth-dim">
          admin@demo.test · Password123!
        </p>
      )}
    </AuthLayout>
  );
}
